import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import { useSelector } from "react-redux";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

const reports = [
  { id: "purchase", label: "Purchase Report by Order" },
  { id: "aggregated_purchase", label: "Aggregated Purchase Report" },
];

export const Reporting = (props) => {
  const classes = useStyles();

  const cDateIni = new Date();
  const cDateEnd = new Date();
  cDateIni.setDate(cDateIni.getDate() + 1);
  cDateEnd.setDate(cDateEnd.getDate() + 1);
  cDateIni.setHours(0, 0, 0, 0);
  cDateEnd.setHours(23, 59, 59, 0);

  const currentUserStore = useSelector((state) => state.current_user);

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [dateRange, setDateRange] = useState([cDateIni, cDateEnd]);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [companyLocationSelected, setCompanyLocationSelected] = useState(null);
  const [reportSelected, setReportSelected] = useState(null);

  const handleChangeCompanyLocations = (e, value) => {
    setCompanyLocationSelected(value);
  };

  const handleChangeReport = (e, value) => {
    setReportSelected(value);
  };

  const handleChangeRangeDate = (e) => {
    const { name, value } = e.target;
    setDateRange((prevState) => {
      if (name === "start_date") {
        value.setHours(0, 0, 0, 0);
        prevState[0] = value;
      } else {
        value.setHours(23, 59, 59, 0);
        prevState[1] = value;
      }
      return [prevState[0], prevState[1]];
    });
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          {`Are you sure you want to export ${reportSelected?.label}?`}
          <br />
          <br />
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Exporting report <br />
              This may take some time relative to the number of records
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while exporting the report
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The report was exported successfully.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    let startDate = dateRange[0];
    let endDate = dateRange[1];

    const companyLocationId = companyLocationSelected.id;
    const startDateParam = new Date(
      startDate.getTime() - startDate.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const endDateParam = new Date(
      endDate.getTime() - endDate.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];

    let reportAPI = "";

    switch (reportSelected?.id) {
      case "purchase":
        reportAPI = `${API_URL}reports/export-purchases-by-order/?company_location=${companyLocationId}&start_date=${startDateParam}&end_date=${endDateParam}`;
        break;
      case "aggregated_purchase":
        reportAPI = `${API_URL}reports/export-aggregated-purchases/?company_location=${companyLocationId}&start_date=${startDateParam}&end_date=${endDateParam}`;
        break;
      default:
        setErrorInfo({
          error: true,
          message: "There was an issue exporting the report; API not found.",
        });
        return;
    }

    await backend
      .get(`${reportAPI}`, { responseType: "blob" })
      .then(({ data }) => {
        const objectUrl = URL.createObjectURL(data);

        const filename = `${companyLocationSelected.name}_${startDateParam}_to_${endDateParam}_${reportSelected.id}.xlsx`;

        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

        setLoadingFlag(false);
        setFinishedFlag(true);
      })
      .catch((error) => {
        console.log(error);
        const errorData = error.response.data;

        setErrorInfo({
          error: true,
          message:
            typeof errorData === "object"
              ? errorData.hasOwnProperty("message")
                ? errorData.message
                : errorData.detail
              : error.message,
        });
      });
  };

  useEffect(() => {
    const getCompanyLocations = async () => {
      await backend
        .get(`${API_URL}companylocations?no_page=1`)
        .then(({ data }) => {
          if (data.length) {
            setCompanyLocations(data);
          }
        });
    };

    const getReports = () => {
      if (reports.length === 1) {
        setReportSelected(reports[0]);
      }
    };

    getCompanyLocations();
    getReports();
  }, []);

  return (
    <Container style={{ minHeight: "80vh" }}>
      {currentUserStore === null ? (
        <></>
      ) : !currentUserStore.is_staff ? (
        <>Permission denied</>
      ) : (
        <>
          <Modal open={confirmModal} onClose={toggleConfirmModal}>
            {confirmModalContent}
          </Modal>
          <Typography component="h6" variant="h6" align="center">
            Reporting
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}></Grid>
            <Grid item xs={12} sm={12}>
              Use this interface to export different report types ex: Purchase
              Report
            </Grid>
            <Grid item xs={12} sm={12}></Grid>
            <Grid item xs={12} sm={12}>
              <InputLabel shrink id="report">
                Report
              </InputLabel>
              <Autocomplete
                fullWidth
                disablePortal
                size="small"
                name="report"
                options={reports}
                getOptionLabel={(option) => `${option.label}`}
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeReport(e, value)}
                helpertext="Select the orders to include in the receipt difference."
                value={reportSelected}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disableToolbar
                  variant="inline"
                  format="MM/dd/yyyy"
                  margin="normal"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  value={dateRange[0]}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                      label="Select start date"
                      variant="standard"
                    />
                  )}
                  onChange={(date) =>
                    handleChangeRangeDate({
                      target: {
                        name: "start_date",
                        value: date,
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disableToolbar
                  variant="inline"
                  format="MM/dd/yyyy"
                  margin="normal"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  value={dateRange[1]}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                      label="Select end date"
                      variant="standard"
                    />
                  )}
                  onChange={(date) =>
                    handleChangeRangeDate({
                      target: {
                        name: "end_date",
                        value: date,
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={12}>
              <InputLabel shrink id="orders">
                Select a Company Location
              </InputLabel>
              <Autocomplete
                fullWidth
                disablePortal
                size="small"
                name="orders"
                options={companyLocations}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeCompanyLocations(e, value)}
                helpertext="Select the orders to include in the receipt difference."
                value={companyLocationSelected}
              />
            </Grid>
            <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => toggleConfirmModal()}
                className={classes.button}
                disabled={
                  companyLocationSelected === null ||
                  reportSelected === null ||
                  loadingFlag
                }
              >
                Export Report
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Reporting;
